'use client';

import React, { type ComponentProps, useEffect, useRef, useState } from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';
import { clsx } from 'clsx';
import { isAfter, isBefore } from 'date-fns';
import { useWindowSize } from 'react-use';

import styles from './styles.module.css';

type BaseProps = {
  children: React.ReactNode | string;
  className?: string;
};

type TimeBoundProps = BaseProps & {
  alwaysVisible?: false;
  startTimestamp: Date;
  endTimestamp: Date;
};

type AlwaysVisibleProps = BaseProps & {
  alwaysVisible: true;
  startTimestamp?: never;
  endTimestamp?: never;
};

type Props = TimeBoundProps | AlwaysVisibleProps;

function Label({ className, children }: ComponentProps<typeof Caption>) {
  return (
    <Caption
      className={clsx(styles.textContent, className)}
      variant="sm-regular"
    >
      {children}
    </Caption>
  );
}

function CampaignBanner({
  children,
  className,
  alwaysVisible,
  startTimestamp,
  endTimestamp,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const { width: windowWidth } = useWindowSize();
  const [repetitions, setRepetitions] = useState(8); // default repetitions until measured

  useEffect(() => {
    if (ref.current && windowWidth) {
      const textWidth = ref.current.offsetWidth;

      if (textWidth <= 0) {
        return;
      }

      // number of repetitions needed to fill the screen + extra buffer for seamless animation on wider screens
      const repetitions = Math.ceil(windowWidth / textWidth) + 6;
      setRepetitions(repetitions);
    }
  }, [windowWidth, children]);

  if (!alwaysVisible) {
    const now = new Date();
    if (!isAfter(now, startTimestamp) || !isBefore(now, endTimestamp)) {
      return null;
    }
  }

  return (
    <div className={clsx(className, styles.container)}>
      <div className={styles.scrollWrapper}>
        {Array.from({ length: repetitions }).map((_, index) => (
          <div key={index} ref={index === 0 ? ref : undefined}>
            {children}
          </div>
        ))}
      </div>
    </div>
  );
}

CampaignBanner.Label = Label;

export { CampaignBanner };
